import {
  classNames,
  formatCurrency,
  transformDateToLocalDateString,
} from "helpers";
import { FleetFireExtinguisherRow } from "services/api/fleet/vehicles/fireextinguishers/types";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import Tooltip from "ui/components/Tooltip/Tooltip";

// Hrvatska množina za "dan": 1 dan, ostalo dana.
const dayLabel = (days: number): string => (days === 1 ? "dan" : "dana");

interface ExpirationStatus {
  className: string;
  title: string;
  message: string;
}

// Status i boja ćelije datuma isteka:
// istekao -> svijetlo crvena, unutar mjesec dana -> svijetlo žuta, inače bez boje.
const getExpirationStatus = (expirationDate: Date | null): ExpirationStatus => {
  if (!expirationDate) {
    return {
      className: "text-slate-900",
      title: "Bez datuma isteka",
      message: "Za ovaj aparat nije unesen datum isteka.",
    };
  }

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const expiry = new Date(expirationDate);
  expiry.setHours(0, 0, 0, 0);

  const oneMonthAhead = new Date(startOfToday);
  oneMonthAhead.setMonth(oneMonthAhead.getMonth() + 1);

  const diffDays = Math.round(
    (expiry.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (expiry < startOfToday) {
    const daysAgo = Math.abs(diffDays);
    return {
      className: "bg-red-100 text-red-700",
      title: "Istekao",
      message: `Aparat je istekao prije ${daysAgo} ${dayLabel(daysAgo)}.`,
    };
  }

  if (expiry <= oneMonthAhead) {
    return {
      className: "bg-yellow-100 text-yellow-800",
      title: "Pred istek",
      message:
        diffDays === 0
          ? "Aparat ističe danas."
          : `Aparat ističe za ${diffDays} ${dayLabel(diffDays)}.`,
    };
  }

  return {
    className: "text-slate-900",
    title: "Važeći",
    message: `Aparat je važeći, ističe za ${diffDays} ${dayLabel(diffDays)}.`,
  };
};

// Stupci za globalnu pretragu vatrogasnih aparata (kroz sva vozila).
// Prepend "Vozilo" stupca koji koristi puni FleetVehicle objekt koji BE vraća uz svaki red.
export const FIRE_EXTINGUISHER_SEARCH_COLUMNS = (
  onVehicleClick: (event: React.MouseEvent, vehicleId: string) => void
): Column[] => [
  {
    title: "Vozilo",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetFireExtinguisherRow) => {
      if (!row.vehicle) return null;
      return (
        <VehicleCell
          vehicle={row.vehicle}
          onClick={(e) => onVehicleClick(e, row.vehicle.id)}
        />
      );
    },
  },
  {
    title: "Naziv",
    field: "name",
    type: ColumnTypes.string,
  },
  {
    title: "Cijena",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetFireExtinguisherRow) => (
      <div className="text-sm text-slate-900 truncate">
        {formatCurrency.format(row.price ?? 0)}
      </div>
    ),
  },
  {
    title: "Datum isteka",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetFireExtinguisherRow) => {
      const status = getExpirationStatus(row.expirationDate);
      return (
        <Tooltip
          decorate={
            <span
              className={classNames(
                "inline-block rounded px-2 py-1 text-sm font-medium",
                status.className
              )}
            >
              {transformDateToLocalDateString(row.expirationDate)}
            </span>
          }
          position="bottom-right"
          size="medium"
        >
          <div className="min-w-[220px]">
            <div className="text-xs uppercase text-slate-400 mb-1 font-semibold">
              {status.title}
            </div>
            <div className="text-sm">{status.message}</div>
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: "Težina",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetFireExtinguisherRow) => (
      <div className="text-sm text-slate-900 truncate">{row.weight} kg</div>
    ),
  },
  {
    title: "Serijski broj",
    field: "serialNumber",
    type: ColumnTypes.string,
  },
];
